import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Toast from '../components/Toast';
import ConfirmModal from '../components/ConfirmModal';
import { MiniPill } from '../components/ui';

function InviteAdminModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', role: 'admin' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) {
      setError('First name, last name and email are required');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api.post('/admin/invite-admin', form);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send invitation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Invite Team Member</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">First Name *</label>
              <input value={form.first_name}
                onChange={(e) => setForm(prev => ({ ...prev, first_name: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-brand-500 focus:border-brand-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Last Name *</label>
              <input value={form.last_name}
                onChange={(e) => setForm(prev => ({ ...prev, last_name: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-brand-500 focus:border-brand-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email *</label>
            <input type="email" value={form.email}
              onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-brand-500 focus:border-brand-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Role *</label>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <button type="button" onClick={() => setForm(prev => ({ ...prev, role: 'admin' }))}
                className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                  form.role === 'admin'
                    ? 'border-brand-500 bg-brand-50 text-brand-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}>
                <div className="font-semibold">Admin</div>
                <div className="text-xs mt-1 font-normal text-gray-500">Full access to dashboard, clients, and team</div>
              </button>
              <button type="button" onClick={() => setForm(prev => ({ ...prev, role: 'standard' }))}
                className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                  form.role === 'standard'
                    ? 'border-brand-500 bg-brand-50 text-brand-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}>
                <div className="font-semibold">Standard</div>
                <div className="text-xs mt-1 font-normal text-gray-500">Dashboard view only, no client or team management</div>
              </button>
            </div>
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <div className="flex justify-end space-x-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="px-4 py-2 text-sm text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
              {submitting ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function memberName(m) {
  return m.first_name ? `${m.first_name} ${m.last_name || ''}`.trim() : (m.name || m.email || '—');
}
function memberInitials(m) {
  if (m.first_name || m.last_name) {
    return `${(m.first_name || '')[0] || ''}${(m.last_name || '')[0] || ''}`.toUpperCase() || '·';
  }
  if (m.name) return m.name.split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase();
  return (m.email || '?')[0].toUpperCase();
}

export default function TeamPage() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'error' });
  const [search, setSearch] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const { user: currentUser } = useAuth();

  const loadTeam = useCallback(async () => {
    try {
      const res = await api.get('/admin/team');
      setTeam(res.data);
    } catch (err) {
      console.error('Failed to load team:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  const handleResendInvite = async (userId) => {
    try {
      await api.post(`/admin/resend-invite/${userId}`);
      setToast({ message: 'Invite resent successfully', type: 'success' });
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to resend invite', type: 'error' });
    }
  };

  const handleDeleteUser = (userId, userName) => {
    setConfirmAction({
      title: 'Remove Team Member',
      message: `Are you sure you want to remove ${userName}? This cannot be undone.`,
      confirmText: 'Remove', confirmStyle: 'danger',
      onConfirm: async () => {
        setConfirmAction(null);
        try { await api.delete(`/admin/users/${userId}`); loadTeam(); }
        catch (err) { setToast({ message: err.response?.data?.error || 'Failed to remove team member', type: 'error' }); }
      },
    });
  };

  const handleToggleRole = (userId, currentRole, name) => {
    const newRole = currentRole === 'admin' ? 'standard' : 'admin';
    const roleLabel = newRole === 'admin' ? 'Admin' : 'Standard';
    const accessDesc = newRole === 'admin'
      ? 'They will gain full access to clients, team management, and settings.'
      : 'They will only be able to view the dashboard and process submissions.';
    setConfirmAction({
      title: 'Change Role',
      message: `Change ${name} from ${currentRole === 'admin' ? 'Admin' : 'Standard'} to ${roleLabel}?\n\n${accessDesc}`,
      confirmText: `Make ${roleLabel}`, confirmStyle: 'warning',
      onConfirm: async () => {
        setConfirmAction(null);
        try { await api.patch(`/admin/users/${userId}/role`, { role: newRole }); loadTeam(); }
        catch (err) { setToast({ message: err.response?.data?.error || 'Failed to update role', type: 'error' }); }
      },
    });
  };

  if (loading) return <div className="text-center py-12 text-gray-500">Loading team…</div>;

  const filtered = team.filter(m => {
    if (!search) return true;
    const q = search.toLowerCase();
    return memberName(m).toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q);
  });

  return (
    <div className="max-w-5xl mx-auto">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: 'error' })} />

      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-[28px] leading-none text-ink dark:text-gray-100">Team</h1>
          <p className="text-gray-500 dark:text-gray-400 text-[14.5px] mt-2 max-w-[680px]">
            Manage your accountancy team's access. Admin users can manage clients and the team itself. Standard users can process payroll only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email…"
            className="w-60 rounded-md border-gray-300 text-[13.5px] focus:border-brand-500 focus:ring-brand-500" />
          <button onClick={() => setShowInvite(true)}
            className="px-4 py-2 rounded-md text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700">
            + Invite Team Member
          </button>
        </div>
      </header>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl ring-1 ring-gray-200 px-6 py-12 text-center text-gray-500">
          No team members match your search.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(m => {
            const isSelf = m.id === currentUser?.id;
            const name = memberName(m);
            return (
              <div key={m.id}
                className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-brand-300 hover:shadow-sm grid items-center gap-4 px-5 py-3.5"
                style={{ gridTemplateColumns: '40px minmax(0, 1fr) 110px 100px 140px 200px' }}>
                <span className="grid place-items-center w-10 h-10 rounded-full bg-brand-100 text-brand-700 text-[14px] font-bold">{memberInitials(m)}</span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[15px] font-semibold text-gray-900 dark:text-gray-100 truncate">{name}</span>
                    {isSelf && <MiniPill tone="brand">You</MiniPill>}
                  </div>
                  <p className="text-[13px] text-gray-500 dark:text-gray-400 truncate">{m.email}</p>
                </div>
                <MiniPill tone={m.role === 'admin' ? 'purple' : 'sky'}>{m.role === 'admin' ? 'Admin' : 'Standard'}</MiniPill>
                <MiniPill tone={m.invite_accepted ? 'emerald' : 'amber'}>{m.invite_accepted ? 'Active' : 'Invite pending'}</MiniPill>
                <span className="text-[12.5px] tabular text-gray-500 dark:text-gray-400">
                  Joined {new Date(m.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
                <div className="flex items-center justify-end gap-1">
                  {!isSelf && (
                    <>
                      {!m.invite_accepted && (
                        <button onClick={() => handleResendInvite(m.id)}
                          className="px-2.5 py-1 rounded-md text-[12.5px] font-medium text-brand-700 hover:bg-brand-50">Resend invite</button>
                      )}
                      <button onClick={() => handleToggleRole(m.id, m.role, name)}
                        className="px-2.5 py-1 rounded-md text-[12.5px] font-medium text-gray-600 hover:bg-gray-100"
                        title={m.role === 'admin' ? 'Change to Standard' : 'Change to Admin'}>
                        Change role
                      </button>
                      <button onClick={() => handleDeleteUser(m.id, name)}
                        className="px-2.5 py-1 rounded-md text-[12.5px] font-medium text-rose-600 hover:bg-rose-50">Remove</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showInvite && (
        <InviteAdminModal
          onClose={() => setShowInvite(false)}
          onSuccess={() => { setShowInvite(false); loadTeam(); setToast({ message: 'Invitation sent successfully!', type: 'success' }); }}
        />
      )}

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          confirmText={confirmAction.confirmText}
          confirmStyle={confirmAction.confirmStyle}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}
